// Inline-editable text. Click to enter edit-mode, Enter/blur commits,
// Esc cancels. Used for project name, phase name, task title.

import { useEffect, useRef, useState } from "preact/hooks";
import { toastError } from "../lib/toast";

interface Props {
  value: string;
  onSave: (next: string) => void | Promise<void>;
  /** Tailwind classes applied to both the display + input wrapper. */
  class?: string;
  /** Optional placeholder shown when value is empty. */
  placeholder?: string;
  /** Minimum visual width while editing. */
  minWidth?: string;
  /** Aria label for screen readers. */
  ariaLabel?: string;
  /** Disable editing (e.g. permission gated). */
  readOnly?: boolean;
}

export function InlineEdit(props: Props): preact.JSX.Element {
  const { value, onSave, class: cls = "", placeholder, ariaLabel, readOnly } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = async (): Promise<void> => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== value) {
      try {
        await onSave(next);
      } catch {
        // Revert the optimistic draft; ohne catch wäre das eine unhandled
        // rejection und die UI zeigt stillschweigend den nicht gespeicherten
        // Wert.
        setDraft(value);
        toastError("Änderung konnte nicht gespeichert werden.");
      }
    } else {
      setDraft(value);
    }
  };

  if (readOnly || !editing) {
    return (
      <button
        type="button"
        onClick={() => !readOnly && setEditing(true)}
        class={`text-left truncate rounded px-1 -mx-1 hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors ${
          readOnly ? "cursor-default" : ""
        } ${cls}`}
        // Sichtbarer Wert ZUERST im Namen (WCAG 2.5.3 „Label in Name"):
        // vorher hiess der Knopf nur „Projekt-Namen bearbeiten", und ein
        // Screenreader las den Projektnamen gar nicht vor.
        aria-label={value ? `${value}, ${ariaLabel ?? "Bearbeiten"}` : ariaLabel ?? "Bearbeiten"}
      >
        {value || (
          <span class="text-[color:var(--color-on-surface-variant)]/60 italic">
            {placeholder ?? "—"}
          </span>
        )}
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
      aria-label={ariaLabel}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          void commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setDraft(value);
          setEditing(false);
        }
      }}
      class={`rounded border border-[color:var(--color-primary)] bg-[color:var(--color-surface-container-lowest)] px-1 -mx-1 focus:outline-none ring-2 ring-[color:var(--color-primary)] ring-offset-2 ring-offset-[color:var(--color-surface-container-lowest)] ${cls}`}
      style={{ minWidth: props.minWidth ?? "8rem" }}
    />
  );
}
